// Analytics warehouse selection helpers, shared by every analytics page that
// filters by warehouse (Sales over Time, Sales by Product, Supply Chain Cost,
// Inventory Risk, Lost Sales, Inventory Health).
//
// Multi-select state is an array of warehouse options. An EMPTY array means
// "All warehouses" — no warehouse filter at all, i.e. every warehouse of the
// client, including ones the client's analytics configuration does not list.
// It is never represented as "every option in the picker".

// Warehouse `name` alone is not unique (several "Harrisburg" locations can
// exist on one client) — pair it with warehouse_code so options are
// distinguishable.
export const warehouseOptionLabel = (warehouse) => {
  if (!warehouse) return "";
  if (warehouse.label) return warehouse.label;
  if (warehouse.name && warehouse.warehouse_code) {
    return `${warehouse.name} (${warehouse.warehouse_code})`;
  }
  return warehouse.name || warehouse.warehouse_code || "";
};

const warehouseKey = (warehouse) =>
  warehouse && warehouse._id != null ? String(warehouse._id) : "";

// Deduplicated by id and ordered by label (then id) — the same selection
// always renders, serializes and caches identically.
export const normalizeWarehouseSelection = (warehouses) => {
  const byId = new Map();
  (warehouses || []).forEach((warehouse) => {
    const id = warehouseKey(warehouse);
    if (id && !byId.has(id)) byId.set(id, warehouse);
  });
  return [...byId.values()].sort((a, b) => {
    const byLabel = warehouseOptionLabel(a).localeCompare(warehouseOptionLabel(b));
    return byLabel || warehouseKey(a).localeCompare(warehouseKey(b));
  });
};

// Sorted, deduplicated id list for requests and cache keys. [] = All.
export const selectedWarehouseIds = (warehouses) =>
  [...new Set((warehouses || []).map(warehouseKey).filter(Boolean))].sort();

// Stable cache-key fragment for a selection ("" = All warehouses).
export const warehouseIdsKey = (warehouseIds) =>
  [...new Set((warehouseIds || []).filter(Boolean).map(String))].sort().join(",");

// Writes the warehouse scope onto URLSearchParams. One warehouse keeps the
// legacy `warehouse_id=<id>` form; several use `warehouse_id[$in][]` (the
// same convention as `source_channel[$in][]`); none sends nothing (All).
export const appendWarehouseIds = (params, warehouseIds) => {
  const ids = warehouseIdsKey(warehouseIds).split(",").filter(Boolean);
  if (ids.length === 1) {
    params.set("warehouse_id", ids[0]);
  } else {
    ids.forEach((id) => params.append("warehouse_id[$in][]", id));
  }
  return params;
};

// Summary for a compact multi-select control.
export const warehouseSelectionSummary = (
  warehouses,
  { allLabel = "All warehouses" } = {},
) => {
  const list = normalizeWarehouseSelection(warehouses);
  if (!list.length) return allLabel;
  if (list.length === 1) return warehouseOptionLabel(list[0]);
  return `${list.length} warehouses`;
};

// Default for single-select pages that require a warehouse: the client's
// default warehouse when the analytics picker offers it, else the first
// offered warehouse.
export const pickDefaultAnalyticsWarehouse = (response) => {
  if (response?.default_warehouse?._id) return response.default_warehouse;
  const rows = Array.isArray(response?.data) ? response.data : [];
  return rows.find((row) => row?._id) || null;
};
